import React, { useCallback, useMemo, useState } from 'react';
import { LogoutOutlined } from '@hangar/react-icons/core/interaction/LogoutOutlined';
import { Header, Sidebar } from '@ecargo/web-components';
import type { NavigateFunction } from 'react-router-dom';
import { useAuth } from '../../../../hooks/auth';
import { useTranslation } from '../../../../hooks/i18n';
import { EcargoSidebarExpandedWidth240, LayoutContainer } from './DashboardLayout.styles';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES, isMenuItemActive } from '../../../../router/routes';
import { DashboardLayoutProps } from './DashboardLayout.types';
import { useAssets } from '../../../../hooks/utils';
import { useSidebarUser } from './useSidebarUser';
import type { MenuItem } from '../../../../config/layout/types';

/** Mirror of eCargo {@link Sidebar} `items` / `itemsBottom` entries. */
type EcargoSidebarItem = {
  id: string | number;
  name: React.ReactNode;
  href?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  badge?: React.ReactNode;
  children?: EcargoSidebarItem[];
};

const buildTopItems = (menuItems: MenuItem[], navigate: NavigateFunction): EcargoSidebarItem[] =>
  menuItems.map((item) => ({
    id: item.path,
    name: item.text,
    href: item.path,
    onClick: () => {
      navigate(item.path);
    },
    icon: React.createElement(item.icon),
  }));

const buildBottomItems = (
  systemItems: DashboardLayoutProps['systemItems'],
  t: (k: string) => string,
  navigate: NavigateFunction,
  onLogout: () => void
): EcargoSidebarItem[] => {
  const system: EcargoSidebarItem[] = (systemItems ?? []).map((item) => ({
    id: item.path,
    name: t(item.text),
    href: item.path,
    onClick: () => {
      navigate(item.path);
    },
    icon: React.createElement(item.icon),
  }));

  const logoutItem: EcargoSidebarItem = {
    id: 'ecargo-sign-out',
    name: t('dashboardLayout.signOut'),
    href: '/__ecargo_logout__',
    onClick: onLogout,
    icon: <LogoutOutlined />,
  };

  return [...system, logoutItem];
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ menuItems, systemItems = [] }) => {
  const { logout, isAuthenticated } = useAuth();
  const { assets } = useAssets();
  const { t } = useTranslation();
  const { displayName, userPhoto } = useSidebarUser();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(async () => {
    await logout(true);
    globalThis.location.href = ROUTES.LOGIN;
  }, [logout]);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((c) => !c);
  }, []);

  const topItems = useMemo(
    () => (isAuthenticated ? buildTopItems(menuItems, navigate) : []),
    [isAuthenticated, menuItems, navigate]
  );

  const bottomItems = useMemo(
    () =>
      isAuthenticated
        ? buildBottomItems(systemItems, t, navigate, () => {
            void handleLogout().catch(() => {});
          })
        : [],
    [isAuthenticated, systemItems, t, navigate, handleLogout]
  );

  const activeItemId = useMemo(() => {
    const top = menuItems.find((i) => isMenuItemActive(location.pathname, i));
    if (top) {
      return top.path;
    }
    for (const s of systemItems) {
      if (location.pathname === s.path) {
        return s.path;
      }
    }
    return undefined;
  }, [location.pathname, menuItems, systemItems]);

  const hideChrome =
    location.pathname === ROUTES.LOGIN ||
    location.pathname.startsWith(ROUTES.UNSUBSCRIBE) ||
    location.pathname.startsWith(ROUTES.FEEDBACK_THANK_YOU);

  const noContentPadding = location.pathname.startsWith(ROUTES.UNSUBSCRIBE) || location.pathname.startsWith('/feedback');

  const mainOutlet = (
    <LayoutContainer $embedded={!hideChrome} $noPadding={noContentPadding}>
      <Outlet />
    </LayoutContainer>
  );

  if (hideChrome) {
    return mainOutlet;
  }

  return (
    <>
      <EcargoSidebarExpandedWidth240 />
      <Header
        logoSrc={assets.logos.latamCargo}
        onLogoClick={() => navigate('/')}
        onMenuClick={toggleSidebar}
        isSidebarOpen={!isCollapsed}
        showMenuIcon
      />
      {/* `userCompany` vacío: contrato eCargo; completar cuando perfil/BFF exponga compañía (ECADCA-530-review). */}
      <Sidebar
        open={!isCollapsed}
        onOpenChange={(open) => setIsCollapsed(!open)}
        items={topItems}
        itemsBottom={bottomItems}
        userName={displayName}
        userCompany=""
        userAvatarSrc={userPhoto ?? undefined}
        menuTitle={t('dashboardLayout.menu')}
        activeItemId={activeItemId}
        loading={false}
      >
        {mainOutlet}
      </Sidebar>
    </>
  );
};
